/**
 * Biometric Input Normalizer
 * 
 * Normalizes biometric slider inputs to a standard 0-1 range
 * for consistent mapping to morph channels.
 * 
 * Each biometric has defined min/max ranges based on fetal development data.
 */

/**
 * Biometric input definitions with ranges
 * 
 * Internal names are in English, display names (Turkish) are separate.
 * Ranges are based on typical fetal measurements at various gestational ages.
 */
export const BIOMETRIC_DEFINITIONS = {
  // Turkish: BPD - Biparietal Diameter (mm)
  bpd: {
    id: 'bpd',
    displayName: 'BPD',
    unit: 'mm',
    min: 35,
    max: 95,
    reference: 57,  // 24 week reference
    description: 'Biparietal diameter - head width measurement'
  },

  // Turkish: HC - Head Circumference (mm)
  hc: {
    id: 'hc',
    displayName: 'HC',
    unit: 'mm',
    min: 140,
    max: 350,
    reference: 219,
    description: 'Head circumference'
  },

  // Turkish: Önarka baş - Occipitofrontal Diameter (mm)
  ofd: {
    id: 'ofd',
    internalAlias: 'onarka_bas',
    displayName: 'Önarka Baş',
    unit: 'mm',
    min: 45,
    max: 115,
    reference: 75,
    description: 'Occipitofrontal diameter - head length'
  },

  // Turkish: Fromen - Forehead measurement (mm)
  fromen: {
    id: 'fromen',
    displayName: 'Fromen',
    unit: 'mm',
    min: 15,
    max: 55,
    reference: 31,
    description: 'Forehead height measurement'
  },

  // Turkish: Burun - Nose length (mm)
  burun: {
    id: 'burun',
    displayName: 'Burun',
    unit: 'mm',
    min: 3,
    max: 18,
    reference: 8,
    description: 'Nasal bone / nose length'
  },

  // Turkish: Göztepe - Inter-ocular distance (mm)
  goztepe: {
    id: 'goztepe',
    displayName: 'Göztepe',
    unit: 'mm',
    min: 10,
    max: 40,
    reference: 23,
    description: 'Inter-ocular distance / eye spacing'
  },

  // Turkish: BiocÇap - Biocular diameter / face width (mm)
  bioc_cap: {
    id: 'bioc_cap',
    displayName: 'BiocÇap',
    unit: 'mm',
    min: 22,
    max: 65,
    reference: 41,
    description: 'Biocular diameter - midface width'
  },

  // Turkish: Çene - Chin/mandible length (mm)
  cene: {
    id: 'cene',
    displayName: 'Çene',
    unit: 'mm',
    min: 8,
    max: 42,
    reference: 20,
    description: 'Mandible / chin length'
  },

  // Turkish: Ağızçapı - Mouth width (mm)
  agiz_capi: {
    id: 'agiz_capi',
    displayName: 'Ağızçapı',
    unit: 'mm',
    min: 5,
    max: 25,
    reference: 12,
    description: 'Mouth width measurement'
  },

  // Turkish: Göz - Eye size/depth (mm)
  goz: {
    id: 'goz',
    displayName: 'Göz',
    unit: 'mm',
    min: 4,
    max: 18,
    reference: 9,
    description: 'Eye size measurement'
  },

  // Turkish: Üst Dudak - Upper lip (mm)
  ust_dudak: {
    id: 'ust_dudak',
    displayName: 'Üst Dudak',
    unit: 'mm',
    min: 2,
    max: 12,
    reference: 6,
    description: 'Upper lip measurement'
  }
};

/**
 * BiometricNormalizer class
 * 
 * Handles normalization of raw biometric values to normalized range.
 */
export class BiometricNormalizer {
  constructor() {
    this.definitions = BIOMETRIC_DEFINITIONS;
  }

  /**
   * Normalize a single biometric value to 0-1 range
   * 
   * @param {string} biometricId - The biometric identifier
   * @param {number} rawValue - The raw measurement value
   * @returns {number} Normalized value between 0 and 1
   */
  normalize(biometricId, rawValue) {
    const def = this.definitions[biometricId];
    if (!def) {
      console.warn(`Unknown biometric: ${biometricId}`);
      return 0.5; // Default to middle
    }

    const { min, max } = def;
    const clamped = Math.max(min, Math.min(max, rawValue));
    return (clamped - min) / (max - min);
  }

  /**
   * Normalize to centered range (-1 to 1) based on reference value
   * 
   * @param {string} biometricId - The biometric identifier
   * @param {number} rawValue - The raw measurement value
   * @returns {number} Normalized value between -1 and 1 (0 = reference)
   */
  normalizeCentered(biometricId, rawValue) {
    const def = this.definitions[biometricId];
    if (!def) {
      console.warn(`Unknown biometric: ${biometricId}`);
      return 0;
    }

    const { min, max, reference } = def;
    const clamped = Math.max(min, Math.min(max, rawValue));
    
    // Calculate deviation from reference
    if (clamped >= reference) {
      // Above reference: map to 0-1
      return (clamped - reference) / (max - reference);
    } else {
      // Below reference: map to -1-0
      return (clamped - reference) / (reference - min);
    }
  }

  /**
   * Normalize all biometric inputs at once
   * 
   * @param {Object} rawMeasurements - Object with raw measurement values
   * @returns {Object} Object with normalized values (centered, -1 to 1)
   */
  normalizeAll(rawMeasurements) {
    const normalized = {};
    
    for (const [key, def] of Object.entries(this.definitions)) {
      // Handle alias (onarka_bas -> ofd)
      const rawKey = def.internalAlias || key;
      const rawValue = rawMeasurements[rawKey] ?? rawMeasurements[key] ?? def.reference;
      normalized[key] = this.normalizeCentered(key, rawValue);
    }
    
    return normalized;
  }

  /**
   * Get reference value for a biometric
   */
  getReference(biometricId) {
    return this.definitions[biometricId]?.reference ?? 0;
  }

  /**
   * Get range for a biometric
   */
  getRange(biometricId) {
    const def = this.definitions[biometricId];
    return def ? { min: def.min, max: def.max } : { min: 0, max: 100 };
  }

  /**
   * Denormalize a value back to raw units
   */
  denormalize(biometricId, normalizedValue) {
    const def = this.definitions[biometricId];
    if (!def) return 0;
    
    const { min, max, reference } = def;
    
    if (normalizedValue >= 0) {
      return reference + normalizedValue * (max - reference);
    } else {
      return reference + normalizedValue * (reference - min);
    }
  }
}

// Export singleton instance
export const biometricNormalizer = new BiometricNormalizer();

export default BiometricNormalizer;
